/**
 * Per-line tax rates on Direct Bills - client copy for the review screen and
 * JobTread payloads.
 *
 * The reviewer can set any extracted line's tax rate (0% for an exempt line,
 * 5% GST only from an out-of-province supplier, etc.). Once a line has its own
 * rate the bill is taxed line by line: each line at its own rate, every other
 * line at the bill's rate (`lineTaxDefaultRate`, kept from when the first line
 * rate was set). Each line is rounded to cents, the way Xero does it, and the
 * bill's tax is the sum of the lines.
 *
 * Canada (province with PST): a line's rate is split into the recoverable part
 * (GST, up to the province's recoverable rate) and the non-recoverable part
 * (PST, the rest). BC line at 12% = GST 5% + PST 7%; at 5% = GST only.
 *
 * Mirrors server/src/utils/lineTaxRates.js (a server test compares the two -
 * change both together).
 */

function round(n, dp = 2) {
  const f = Math.pow(10, dp);
  return Math.round(Number(((Number(n) || 0) * f).toFixed(6))) / f;
}

/** The line's own tax rate (%), or null when it follows the bill's rate. */
export function lineTaxRateOverride(line) {
  const v = line ? line.taxRateOverride : null;
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function hasLineTaxOverrides(lines) {
  return (lines || []).some(l => lineTaxRateOverride(l) !== null);
}

/**
 * Tax for each line. `recoverableRatePct` only for a Canadian province with
 * PST (the GST rate); leave it null elsewhere and all tax is recoverable.
 * Returns [{ ratePct, overridden, recoverable, nonRecoverable, tax }].
 */
export function planLineTax(lines, { defaultRatePct = 0, recoverableRatePct = null } = {}) {
  return (lines || []).map(line => {
    const amount = Number(line && line.amount) || 0;
    const override = lineTaxRateOverride(line);
    const ratePct = override !== null ? override : (Number(defaultRatePct) || 0);
    const recoverableRate = recoverableRatePct == null ? ratePct : Math.min(ratePct, Number(recoverableRatePct) || 0);
    const nonRecoverableRate = round(ratePct - recoverableRate, 3);
    const recoverable = round(amount * recoverableRate / 100);
    const nonRecoverable = round(amount * nonRecoverableRate / 100);
    return {
      ratePct,
      overridden: override !== null,
      recoverable,
      nonRecoverable,
      tax: round(recoverable + nonRecoverable)
    };
  });
}

export function sumLineTax(plan) {
  return round((plan || []).reduce((s, p) => s + p.tax, 0));
}
